import { useRecoilState, useRecoilValue } from 'recoil';
import { SearchableSelect } from 'components/core';
import { builderAnswersState, visualizationsState } from 'states';
import { detectCadre } from 'helpers/kpiBuilder/templateKpi';
import { TemplateCadre } from 'helpers/kpiBuilder/types';
import { BuilderSection } from './BuilderSection';
import { OptionCards } from './OptionCards';

const CADRES = [
  { id: 'keep', label: 'Keep as it is', desc: 'Use the same learners as the KPI you copied.' },
  { id: 'inService', label: 'In-service professionals', desc: 'ANMs and ANM faculty. Uses the Facility Type and Block filters.' },
  { id: 'inServiceAnm', label: 'In-service ANMs only', desc: 'ANMs without faculty. Uses the Facility Type and Block filters.' },
  { id: 'preService', label: 'Pre-service students', desc: 'ANM students. Uses the Institute Type and Institute filters.' },
];
const NAMES: Record<string, string> = { preService: 'pre-service students', inService: 'in-service professionals', inServiceAnm: 'in-service ANMs only', mixed: 'a mix of learners', none: 'all learners' };

// Start from an existing KPI: pick one that already works, then change only what differs.
export const TemplateStep = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  const visualizations = useRecoilValue(visualizationsState).filter((v) => v.visualizationCode && v.data?.[0]?.data?.query);
  const label = (v: { title: string; visualizationCode: string }) => `${v.title} (${v.visualizationCode})`;
  const t = a.template;

  const pick = (text: string) => {
    const v = visualizations.find((x) => label(x) === text);
    if (!v) return;
    const d = v.data[0];
    setA({ ...a, template: { ...t, code: v.visualizationCode, title: v.title, chartType: d.info.chartType, handlerKey: d.info.handlerKey ?? '', query: d.data.query, config: d.additionalConfig?.config ?? '{}' } });
  };

  return (
    <>
      <BuilderSection title="Which KPI do you want to copy?" hint="Best for KPIs that need special handling and cannot be built from questions. The copy keeps the original's query, chart and handler, so it draws the same way.">
        <SearchableSelect options={visualizations.map(label)} selected={t.code ? label({ title: t.title, visualizationCode: t.code }) : ''} placeholder="Choose a KPI" onChange={pick} />
        {t.code && (
          <div className="small text-secondary mt-2">
            Chart: {t.chartType}{t.handlerKey ? `, handler: ${t.handlerKey}` : ''}. It counts {NAMES[detectCadre(t)]}.
          </div>
        )}
      </BuilderSection>
      {t.code && !t.query && <div className="alert alert-warning">The KPI this was copied from can no longer be found, so its query cannot be changed here. What is saved is kept as it is.</div>}
      {t.code && (
        <BuilderSection title="Which learners should it count?" hint="Switching the cadre changes the learner filter in every query and the matching location filters. Other filters in the copied KPI, such as channel or level, are kept as they are, so check them.">
          <OptionCards cards={CADRES} selected={[t.cadre]} onSelect={(id) => setA({ ...a, template: { ...t, cadre: id as TemplateCadre } })} />
        </BuilderSection>
      )}
    </>
  );
};
